import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { canManageUsers } from "@equipped/core/auth/roles";
import { BackendClient, BackendError } from "@equipped/core/backend/client";
import { createProviderAuthStorage } from "./authStorage";
import { createAdminKeyClient, createBackendClient } from "../lib/backendClient";
import { DEFAULT_ADMIN_PROVIDER_ID } from "../config/adminDeployment.config";
import { useProvider } from "../providers/ProviderContext";

/**
 * Provider-scoped version of the reusable `apps/admin-web` auth layer (docs/345 v0).
 * With the default provider its client, requests, and persisted keys are identical
 * to the prior single-provider implementation; another provider gets an isolated
 * client and storage namespace.
 *
 * The two ways to authenticate this dashboard (mirrors `apps/admin/ui/
 * session.tsx`'s email/password path 1:1; **Apple sign-in has no web SDK from
 * Apple** — RN's `apps/admin` reaches it only via `expo-apple-authentication`,
 * a native module, so it's out of scope for this browser-only app, docs/325).
 * `key` is the shared `X-Admin-Key` — a single-admin convenience with no
 * password account behind it (worker/src/index.ts `handleAdmin`).
 */
export type AdminAuthMode = "password" | "key";

export interface AdminAuthUser {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
}

interface AuthState {
  /** False until the initial storage restore attempt has settled. */
  ready: boolean;
  mode: AdminAuthMode | null;
  user: AdminAuthUser | null;
  /** True once `user` (password mode) or `mode === "key"` clears the admin-role gate. */
  isAdmin: boolean;
  /** An authenticated `BackendClient` — `Authorization: Bearer` in password mode,
   *  `X-Admin-Key` in key mode (see `lib/backendClient.ts`) — or `null` before
   *  sign-in / when `VITE_BACKEND_URL` is unset. */
  client: BackendClient | null;
  /** First-argument `token` every `BackendClient.admin*` call expects — the
   *  session bearer in password mode, the raw key in key mode (its value is
   *  actually ignored on the wire in key mode; see `adminKeyFetch`). */
  token: string | null;
  busy: boolean;
  error: string | null;
  backendConfigured: boolean;
  /** Active provider identity/origin. All credentials and clients are scoped to these values. */
  providerId: string;
  backendUrl: string | null;
  signInWithPassword: (
    email: string,
    password: string,
    opts?: { register?: boolean; invite?: string; firstName?: string; lastName?: string },
  ) => Promise<void>;
  signInWithAdminKey: (key: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const USER_KEY = "admin-web:user";
const MODE_KEY = "admin-web:mode";
const TOKEN_KEY = "admin-web:token";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { provider } = useProvider();
  const providerId = provider?.id ?? DEFAULT_ADMIN_PROVIDER_ID;
  const backendUrl = provider?.backendUrl ?? null;
  // A provider change remounts the entire session subtree synchronously, so no
  // render can pair provider B's client with provider A's in-memory token.
  return (
    <ProviderAuthSession key={providerId} providerId={providerId} backendUrl={backendUrl}>
      {children}
    </ProviderAuthSession>
  );
}

function ProviderAuthSession({
  children,
  providerId,
  backendUrl,
}: {
  children: React.ReactNode;
  providerId: string;
  backendUrl: string | null;
}) {
  const { setProviderAuth } = useProvider();
  const { authStorage, adminKeyStorage } = useMemo(() => createProviderAuthStorage(providerId), [providerId]);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<AdminAuthMode | null>(null);
  const [user, setUser] = useState<AdminAuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const anonClient = useMemo(() => createBackendClient(backendUrl), [backendUrl]);

  const client = useMemo<BackendClient | null>(() => {
    if (!token || !mode) return null;
    return mode === "key" ? createAdminKeyClient(backendUrl, token) : anonClient;
  }, [mode, token, anonClient, backendUrl]);

  // Restore a persisted session on launch — optimistic for the cached user (same
  // pattern as apps/admin/ui/session.tsx), then re-validated in the background
  // for password mode via `me()`; key mode has no user identity to re-check, so
  // it's restored as-is (an invalid/rotated key simply fails the next admin call).
  useEffect(() => {
    let active = true;
    setReady(false);
    setMode(null);
    setUser(null);
    setToken(null);
    const storedMode = authStorage.getItem(MODE_KEY) as AdminAuthMode | null;
    // The admin-key credential lives in `sessionStorage` (per-tab, cleared on close),
    // not `localStorage`, so a "key" mode restore reads it from `adminKeyStorage`
    // instead — see `authStorage.ts`'s module doc (docs/340 §3).
    const storedToken = storedMode === "key" ? adminKeyStorage.getItem(TOKEN_KEY) : authStorage.getItem(TOKEN_KEY);
    if (!storedMode || !storedToken) {
      setProviderAuth(storedMode === "key" ? "adminKey" : "session", false);
      setReady(true);
      return;
    }
    setProviderAuth(storedMode === "key" ? "adminKey" : "session", true);
    setMode(storedMode);
    setToken(storedToken);
    if (storedMode === "key") {
      setReady(true);
      return;
    }
    const storedUser = authStorage.getItem(USER_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser) as AdminAuthUser);
      } catch {
        /* fall through to background re-check */
      }
    }
    setReady(true);
    if (!anonClient) return;
    anonClient
      .me(storedToken)
      .then((me) => {
        if (!active) return;
        const u: AdminAuthUser = {
          id: me.id,
          email: me.email,
          role: me.role,
          firstName: me.firstName,
          lastName: me.lastName,
        };
        setUser(u);
        authStorage.setItem(USER_KEY, JSON.stringify(u));
      })
      .catch((e) => {
        if (active && e instanceof BackendError && e.status === 401) clearAll();
      });
    return () => {
      active = false;
    };
    // `clearAll` intentionally remains provider-local through these scoped storage deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, anonClient, authStorage, adminKeyStorage]);

  function clearAll() {
    setMode(null);
    setUser(null);
    setToken(null);
    authStorage.removeItem(MODE_KEY);
    authStorage.removeItem(USER_KEY);
    authStorage.removeItem(TOKEN_KEY);
    // Always clear both stores regardless of which mode was active — cheap,
    // defensive, and correct whichever credential (if either) was actually set.
    adminKeyStorage.removeItem(TOKEN_KEY);
    setProviderAuth(mode === "key" ? "adminKey" : "session", false);
  }

  const signInWithPassword = useCallback<AuthState["signInWithPassword"]>(
    async (email, password, opts) => {
      if (!anonClient) throw new Error("Backend URL isn't configured (VITE_BACKEND_URL).");
      setBusy(true);
      setError(null);
      try {
        const result = opts?.register
          ? await anonClient.register(email, password, opts.invite, {
              firstName: opts.firstName,
              lastName: opts.lastName,
            })
          : await anonClient.login(email, password);
        const u: AdminAuthUser = { ...result.user };
        authStorage.setItem(MODE_KEY, "password");
        authStorage.setItem(TOKEN_KEY, result.session);
        authStorage.setItem(USER_KEY, JSON.stringify(u));
        adminKeyStorage.removeItem(TOKEN_KEY); // defensive — no stale key credential lingers
        setMode("password");
        setToken(result.session);
        setUser(u);
        setProviderAuth("session", true);
      } catch (e) {
        setError((e as Error).message);
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [anonClient, authStorage, adminKeyStorage, setProviderAuth],
  );

  const signInWithAdminKey = useCallback<AuthState["signInWithAdminKey"]>(async (key) => {
    if (!backendUrl) throw new Error("Backend URL isn't configured (VITE_BACKEND_URL).");
    setBusy(true);
    setError(null);
    try {
      const probe = createAdminKeyClient(backendUrl, key);
      if (!probe) throw new Error("Backend URL isn't configured.");
      // A key with no admin behind it should fail loudly and immediately, not on
      // whatever the first People-tab fetch happens to be.
      await probe.adminUsers(key);
      authStorage.setItem(MODE_KEY, "key");
      // The shared break-glass key itself goes to `sessionStorage`, not `localStorage`
      // (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3) — see `authStorage.ts`'s module doc.
      adminKeyStorage.setItem(TOKEN_KEY, key);
      authStorage.removeItem(TOKEN_KEY); // defensive — no stale password-session token lingers
      authStorage.removeItem(USER_KEY);
      setMode("key");
      setToken(key);
      setUser(null);
      setProviderAuth("adminKey", true);
    } catch (e) {
      const message = e instanceof BackendError && e.status === 403 ? "That admin key was rejected." : (e as Error).message;
      setError(message);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  }, [backendUrl, authStorage, adminKeyStorage, setProviderAuth]);

  const signOut = useCallback(() => {
    // Delete the server-side session BEFORE clearing local state (docs/340-ADMIN-
    // MANTIS-AND-API-IDEAS.md §3 — `BackendClient` had no `logout()` caller at all, so
    // sign-out only ever cleared local credentials and left the session valid
    // server-side until it expired on its own). Only meaningful in password mode — the
    // shared admin key is a stateless per-request credential, not a session, so
    // there's nothing on the server to delete for it. Fire-and-forget: never blocks the
    // UI on a slow/offline network, and `clearAll()` always runs regardless of outcome
    // (`logout()` is documented idempotent/never-401s anyway).
    if (mode === "password" && client && token) {
      void client.logout(token).catch(() => {
        /* best-effort — local state is cleared either way */
      });
    }
    clearAll();
  }, [mode, client, token, authStorage, adminKeyStorage]);

  const isAdmin = mode === "key" || (!!user && canManageUsers(user.role as "viewer" | "author" | "admin"));

  const value: AuthState = {
    ready,
    mode,
    user,
    isAdmin,
    client,
    token,
    busy,
    error,
    backendConfigured: !!backendUrl,
    providerId,
    backendUrl,
    signInWithPassword,
    signInWithAdminKey,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used within <AuthProvider>");
  return ctx;
}
